import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Icon } from '../../atoms/Icon'
import type { BuildingPopetyIconProps } from './BuildingPopetyIcon.types'

// Building classification configuration - updated to match stops array colors.
// `textColor` is a darkened tone of `color`, readable on the pale `bg` tint and
// on white — used for the type chip / value text where the raw accent would be
// too light (e.g. Residential yellow).
export const buildingClassificationConfig = {
  Retail: {
    iconName: 'retail' as const,
    color: '#D3B39C',
    textColor: '#8A6A52',
    bg: 'rgba(211, 179, 156, 0.1)',
    i18nKey: 'buildingClassification.retail',
  },
  Residential: {
    iconName: 'residential' as const,
    color: '#F9D518',
    textColor: '#9A7B00',
    bg: 'rgba(249, 213, 24, 0.1)',
    i18nKey: 'buildingClassification.residential',
  },
  Public: {
    iconName: 'public' as const,
    color: '#8C8C8C',
    textColor: '#595959',
    bg: 'rgba(140, 140, 140, 0.1)',
    i18nKey: 'buildingClassification.public',
  },
  Other: {
    iconName: 'other' as const,
    color: '#CCCCCC',
    textColor: '#6B6B6B',
    bg: 'rgba(204, 204, 204, 0.1)',
    i18nKey: 'buildingClassification.other',
  },
  Offices: {
    iconName: 'offices' as const,
    color: '#9ecae1',
    textColor: '#2B6C8F',
    bg: 'rgba(158, 202, 225, 0.1)',
    i18nKey: 'buildingClassification.office',
  },
  'Not classified': {
    iconName: 'not-classified' as const,
    color: '#CCCCCC',
    textColor: '#6B6B6B',
    bg: 'rgba(204, 204, 204, 0.1)',
    i18nKey: 'buildingClassification.notClassified',
  },
  notClassified: {
    iconName: 'not-classified' as const,
    color: '#CCCCCC',
    textColor: '#6B6B6B',
    bg: 'rgba(204, 204, 204, 0.1)',
    i18nKey: 'buildingClassification.notClassified',
  },
  Mixed_2: {
    iconName: 'residential' as const,
    color: '#F3733C',
    textColor: '#C2410C',
    bg: 'rgba(243, 115, 60, 0.1)',
    i18nKey: 'buildingClassification.mixedLow',
  },
  Mixed_1: {
    iconName: 'residential' as const,
    color: '#F3A83C',
    textColor: '#B26A00',
    bg: 'rgba(243, 168, 60, 0.1)',
    i18nKey: 'buildingClassification.mixHigh',
  },
  Industrial: {
    iconName: 'industrial' as const,
    color: '#C39EF9',
    textColor: '#6D3FB8',
    bg: 'rgba(195, 158, 249, 0.1)',
    i18nKey: 'buildingClassification.industrial',
  },
  Garage: {
    iconName: 'garage' as const,
    color: '#bbbbbb',
    textColor: '#6B6B6B',
    bg: 'rgba(187, 187, 187, 0.1)',
    i18nKey: 'buildingClassification.garage',
  },
} as const

type BuildingClassificationKey = keyof typeof buildingClassificationConfig

export type BuildingClassificationConfig =
  (typeof buildingClassificationConfig)[BuildingClassificationKey]

/**
 * Resolve a (possibly unknown/empty) classification to its display config,
 * falling back to the neutral "Not classified" entry so callers always get a
 * usable icon, colour and label.
 */
export const getBuildingClassificationConfig = (
  classification?: string | null
): BuildingClassificationConfig =>
  buildingClassificationConfig[classification as BuildingClassificationKey] ??
  buildingClassificationConfig['Not classified']

// Styled components
const IconContainer = styled.div<{
  $withLabel?: boolean
  $background?: string
  $size?: number
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ $withLabel, $size }) => {
    if (!$withLabel) return '0'
    const baseSize = $size || 48
    if (baseSize <= 32) return '4px'
    if (baseSize <= 48) return '6px'
    return '8px'
  }};
  width: ${({ $size }) => $size || 48}px;
  height: ${({ $size, $withLabel }) =>
    $withLabel ? 'auto' : ($size || 48) + 'px'};
  border-radius: ${({ $size }) => {
    const baseSize = $size || 48
    if (baseSize <= 32) return '6px'
    if (baseSize <= 48) return '8px'
    return '10px'
  }};
  background: ${({ $background }) => $background || 'rgba(142, 142, 147, 0.1)'};
  padding: ${({ $withLabel, $size }) => {
    if (!$withLabel) return '0'
    const baseSize = $size || 48
    if (baseSize <= 32) return '6px'
    if (baseSize <= 48) return '8px'
    return '10px'
  }};
  min-height: ${({ $size, $withLabel }) => {
    if (!$withLabel) return 'auto'
    const baseSize = $size || 48
    return baseSize + (baseSize <= 32 ? 16 : baseSize <= 48 ? 20 : 24) + 'px'
  }};
`

const LabelText = styled.span<{ $color?: string; $size?: number }>`
  font-size: ${({ $size }) => {
    const baseSize = $size || 48
    if (baseSize <= 32) return '9px'
    if (baseSize <= 48) return '11px'
    if (baseSize <= 64) return '10px'
    return '14px'
  }};
  font-weight: 500;
  color: ${({ $color }) => $color || '#666666'};
  line-height: 1.2;
  text-align: center;
  word-wrap: break-word;
  max-width: 100%;
`

export const BuildingPopetyIcon: React.FC<BuildingPopetyIconProps> = ({
  classification,
  size = 64,
  className,
  style,
  showLabel = false,
}) => {
  const { t } = useTranslation('explore')
  const config = buildingClassificationConfig[classification]

  // Calculate icon size based on container size
  const getIconSize = () => {
    if (size <= 32) return 'small'
    if (size <= 48) return 'medium'
    if (size <= 64) return 'large'

    return 'xl'
  }

  if (!config) {
    // Return default building icon if classification not found
    return (
      <IconContainer
        $size={size}
        $background="rgba(142, 142, 147, 0.1)"
        className={className}
        style={style}
      >
        <Icon name="building" size={getIconSize()} color="#6B6B6B" />
      </IconContainer>
    )
  }

  const iconElement = (
    <Icon name={config.iconName} size={getIconSize()} color={config.color} />
  )

  return (
    <IconContainer
      $withLabel={showLabel}
      $background={config.bg}
      $size={size}
      className={className}
      style={{ color: config.color, ...style }}
    >
      {iconElement}
      {showLabel && (
        <LabelText $color={config.color} $size={size}>
          {t(config.i18nKey)}
        </LabelText>
      )}
    </IconContainer>
  )
}

export default BuildingPopetyIcon
